import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import TextInputField from "../../../../../utils/Fields/TextInputField";
import { useForm } from "react-hook-form";
import DropZone from "../../../../../utils/Upload/Dropzone";
import ImageList from "../../LandingPageHero.js/ImageList";
import { Button } from "pepsico-ds";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import adminAppValidations from "./adminAppFormValidations";
import { uploadThumbnailAndGiveFileId } from "../../../../../utils/function";
import { postAPI, patchAPI } from "../../../../../API/ApiService";
import { BASE_URL } from "../../../../../constant/apiUrl";
import Loading from "../../../../../utils/Loader";
import fetchAndCacheImage from "../../../../../utils/fetchandCatchImages";
import { useDispatch } from "react-redux";
import { setApp, setUpdateApp } from "../../../../../slices/appadminSlice";
import { useSelector } from "react-redux";
import useSingleImageDrop from "../../../../../hooks/useSingleImageDrop";

const validationSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  link: yup.string().required("Link is required"),
});

export default function AdminAppForm({ updateData, onCancel, isEdit }) {
  const {
    register,
    formState: { errors },
    setValue,
    clearErrors,
    setError,
    getValues,
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onSubmit",
  });

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [thumbnailMessage, setThumbnailError] = useState(false);

  const {
    selectedImage,
    isDropped,
    getRootProps,
    getInputProps,
    handleReplace,
    handleDelete,
    setSelectedImage,
    setIsDropped,
  } = useSingleImageDrop();

  const dispatch = useDispatch();
  const { apps } = useSelector((store) => store.appadmin);
  const onFieldChange = (field, value) => {
    setValue(field, value); // Update the field value
    const validationErrors = adminAppValidations(
      getValues("title"),
      getValues("link")
    );

    if (validationErrors[field]) {
      setError(field, { type: "manual", message: validationErrors[field] });
    } else {
      clearErrors(field); // Clear the error if validation passes
    }
  };
  useEffect(() => {
    if (updateData) {
      // Auto-fill text fields
      if (updateData?.title) setValue("title", updateData?.title);
      if (updateData?.link) setValue("link", updateData?.link);

      // Auto-fill image (assuming `data.thumbnail` is a URL)
      if (updateData?.emblemFileId) {
        fetchImage(updateData.emblemFileId).then((data) => {
          const fakeFile = {
            preview: data,
            file: null, // No File object available
          };
          setSelectedImage([fakeFile]);
          setIsDropped(true);
          setLoading(false);
        });
      }
    }
  }, [updateData, setValue]);
  const fetchImage = async (id) => {
    setLoading(true);
    try {
      const url = await fetchAndCacheImage(id);
      return url;
    } catch (e) {
      console.log(e);
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    const isValid = await trigger(); // validate all fields
    const hasThumbnail = !!selectedImage?.[0];

    if (!hasThumbnail) {
      setThumbnailError(true);
    } else {
      setThumbnailError(false);
    }

    if (!isValid || !hasThumbnail) return;

    try {
      setLoading(true);

      const data = getValues();
      let emblemFileId = updateData?.emblemFileId;
      let newImageUploaded = false;

      // Handle new thumbnail upload
      const newThumbnailFile = selectedImage?.[0];
      if (newThumbnailFile instanceof File) {
        const uploadResponse = await uploadThumbnailAndGiveFileId(
          [selectedImage[0]],
          false
        );
        emblemFileId = uploadResponse[0];
        newImageUploaded = true;
      }

      const payload = {
        title: data.title,
        link: data.link,
        emblemFileId,
      };

      let response;
      if (isEdit) {
        // Edit (PATCH)
        response = await patchAPI(
          `${BASE_URL}/admin/apps/update/${updateData.id}`,
          payload
        );

        if (response?.status === 200) {
          const updatedApp = response?.data?.response;

          if (newImageUploaded && updatedApp?.emblemFileId) {
            try {
              const imageUrl = await fetchAndCacheImage(
                updatedApp.emblemFileId
              );
              updatedApp.image = imageUrl;
            } catch (error) {
              console.error("Image download failed", error);
            }
          } else {
            updatedApp.image = selectedImage[0].preview;
          }

          dispatch(setUpdateApp(updatedApp));
          onCancel();
        }

        if (response?.status === 409) {
          setErr(true);
          setErrorMessage("Another app with same name already exists");
        }
      } else {
        // Create (POST)
        response = await postAPI(`${BASE_URL}/admin/apps/save`, payload);

        if (response?.status === 200) {
          const newApp = response?.data?.response;

          if (newApp?.emblemFileId) {
            try {
              const imageUrl = await fetchAndCacheImage(newApp.emblemFileId);
              newApp.image = imageUrl;
            } catch (error) {
              console.error("Image download failed", error);
            }
          }

          dispatch(setApp([...apps, newApp]));
          onCancel();
        }

        if (response?.status === 409) {
          setErr(true);
          setErrorMessage("Another app with same name already exists");
        }
      }
    } catch (err) {
      console.error("Submit error:", err.message?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="flex flex-col gap-3 w-full" onSubmit={handleSubmitForm}>
      {loading && <Loading />}
      <div className="flex flex-row  gap-10">
        <Box sx={{ display: "flex", flexDirection: "column", gap: "26px" }}>
          <TextInputField
            label="Title"
            errors={errors}
            register={register}
            name="title"
            width={"400px"}
            required
            placeholder="Type here"
            datatestid="text-input-title"
            onChange={(e) => onFieldChange("title", e.target.value)}
            maxLength={30}
          />
          {err && <Typography sx={{ color: "red" }}>{errorMessage}</Typography>}
          {isDropped ? (
            <ImageList
              images={[...selectedImage]}
              onDelete={handleDelete}
              onReplace={(updatedImages) => {
                const file = updatedImages[0]?.file;
                handleReplace(file);
              }}
            />
          ) : (
            <DropZone
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              isDarkTheme={false}
              label="Thumbnail"
              informativeText="Drag and drop your file here (only .jpg,.png)"
              uploadImageLabel="Upload Image"
              isRequired
              data-testid="drop-down"
              highlightRequired={thumbnailMessage}
            />
          )}
        </Box>

        <Box>
          <TextInputField
            label="Link"
            errors={errors}
            register={register}
            name="link"
            width={"400px"}
            required
            placeholder="Type here"
            datatestid="text-input-link"
            onChange={(e) => onFieldChange("link", e.target.value)}
            maxLength={100}
          />
        </Box>
      </div>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "6px",
          alignSelf: "stretch",
        }}
      >
        <Button
          className=""
          size="large"
          style={{}}
          text="Cancel"
          variant="secondary"
          onClick={onCancel}
        />
        <Button
          className=""
          size="large"
          style={{}}
          text="Save"
          variant="primary"
          type="submit"
          data-testid="save"
        />
      </Box>
    </form>
  );
}
